import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Collapse, Divider, IconButton, Typography } from '@mui/material';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import SupportEmailLink from '~/components/SupportEmailLink';
import type {
    BrowserSupportIssueCode,
    BrowserSupportReport,
} from '~/utils/browserSupport';

/**
 * What the user can do about an issue: switch to or update the browser, or
 * restore its graphics acceleration.
 */
type Remedy = 'browser' | 'graphics';

const REMEDY_BY_CODE: Record<BrowserSupportIssueCode, Remedy> = {
    unsupportedBrowser: 'browser',
    noOffscreenCanvas: 'browser',
    noCreateImageBitmap: 'browser',
    noImageBitmapRenderer: 'browser',
    noWebAssembly: 'browser',
    noWebGl2: 'graphics',
    noStencilBuffer: 'graphics',
    softwareRenderer: 'graphics',
    noFloatColorBuffer: 'graphics',
    noFloatLinearFiltering: 'graphics',
    smallTextureLimits: 'graphics',
    probeFailed: 'browser',
};

/**
 * The remedy to offer for a set of issues.
 *
 * A missing graphics capability is the more specific and more often
 * actionable of the two, so it wins when both apply: telling someone whose
 * GPU acceleration is switched off to change browser sends them away from
 * the fix.
 */
function remedyFor(codes: readonly BrowserSupportIssueCode[]): Remedy {
    return codes.some((code) => REMEDY_BY_CODE[code] === 'graphics')
        ? 'graphics'
        : 'browser';
}

interface UnsupportedEnvironmentPageProps {
    report: BrowserSupportReport;
}

/**
 * Full-page notice shown instead of the application when the browser cannot
 * provide something the viewer needs.
 *
 * It names every blocking capability, states what the user can do about it,
 * and keeps the probe's technical description one click away for support.
 */
const UnsupportedEnvironmentPage = ({
    report,
}: UnsupportedEnvironmentPageProps) => {
    const [showDetails, setShowDetails] = useState(false);
    const blocking = report.issues.filter(
        (issue) => issue.severity === 'blocking'
    );

    return (
        <Box
            height="100vh"
            width="100vw"
            display="flex"
            alignItems="center"
            justifyContent="center"
            data-testid="unsupported-environment-page"
        >
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                gap={1}
                // Long driver strings in the diagnostics would otherwise
                // stretch this column across the whole viewport.
                maxWidth={560}
            >
                <Box display="flex" gap={2} alignItems="center">
                    <ErrorOutlineIcon color="error" sx={{ fontSize: 40 }} />
                    <Divider
                        orientation="vertical"
                        variant="middle"
                        flexItem
                        sx={{ height: 60 }}
                    />
                    <Box width={340}>
                        <Typography gutterBottom>
                            <FormattedMessage id="browserSupport.blocked" />
                        </Typography>
                        {blocking.map((issue) => (
                            <Typography
                                key={issue.code}
                                variant="body2"
                                color="text.secondary"
                                data-testid={`browser-support-issue-${issue.code}`}
                            >
                                <FormattedMessage
                                    id={`browserSupport.issue.${issue.code}`}
                                />
                            </Typography>
                        ))}
                    </Box>
                </Box>

                <Box width="100%" mt={2}>
                    <Box display="flex" justifyContent="center">
                        <IconButton
                            onClick={() => setShowDetails(!showDetails)}
                            size="small"
                            aria-label="Technical details"
                            aria-expanded={showDetails}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                px: 1.5,
                            }}
                        >
                            <Typography variant="body2" color="text.secondary">
                                Technical Details
                            </Typography>
                            {showDetails ? (
                                <ExpandLessIcon fontSize="small" />
                            ) : (
                                <ExpandMoreIcon fontSize="small" />
                            )}
                        </IconButton>
                    </Box>
                    <Collapse in={showDetails}>
                        <Box
                            data-testid="browser-support-diagnostics"
                            sx={(theme) => ({
                                backgroundColor: theme.palette.action.hover,
                                borderRadius: 1,
                                p: 2,
                                mt: 1,
                                maxHeight: '200px',
                                overflow: 'auto',
                                fontFamily: 'monospace',
                                fontSize: '12px',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-all',
                            })}
                        >
                            {report.diagnostics}
                        </Box>
                    </Collapse>
                </Box>

                <Box position="absolute" bottom={40} p={3} maxWidth={500}>
                    <Typography textAlign="center" variant="body2" mb={1}>
                        <FormattedMessage
                            id={`browserSupport.remedy.${remedyFor(
                                blocking.map((issue) => issue.code)
                            )}`}
                        />
                    </Typography>
                    <Typography textAlign="center" variant="body2">
                        <FormattedMessage
                            id="browserSupport.contactSupport"
                            values={{ support: <SupportEmailLink /> }}
                        />
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

export default UnsupportedEnvironmentPage;
